import { Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { EgiErrorCode } from '@data/_generated-api/error-mappers/egi-error-code';
import { NOTIFIER } from '@shared/notifier/types';
import { PlatformModeService } from './platform-mode.service';

/** Eén melding per reeks weigeringen: een scherm met vijf mislukte bewaaracties toont er één. */
const READ_ONLY_TOAST_INTERVAL_MS = 8000;

/** Foutcode uit de envelope als de api het verzoek weigerde door de platformmodus, anders undefined. */
export function platformModeRefusal(err: HttpErrorResponse): number | undefined {
  if (err.status !== 423 && err.status !== 503) {
    return undefined;
  }
  const errors = (err.error as { errors?: { errorCode?: number }[] } | null)?.errors;
  const codes = Array.isArray(errors) ? errors.map(e => e?.errorCode) : [];
  return codes.find(c => c === EgiErrorCode.PlatformUnavailable || c === EgiErrorCode.PlatformReadOnly);
}

/**
 * Weigeringen door de platformmodus (issue #51): de modus opnieuw ophalen (de balk verschijnt dan
 * ook in een tab die het hubbericht miste) en bij alleen-lezen een vertaalde melding tonen.
 * UpamErrorInterceptor toont voor deze codes niets en stuurt ze niet naar Sentry.
 *
 * Staat bewust als laatste interceptor (AppModule): hij moet de echte HttpErrorResponse zien,
 * vóór UpamErrorInterceptor die omzet in een gewone response.
 */
@Injectable()
export class PlatformModeInterceptor implements HttpInterceptor {
  // Lui via de injector: PlatformModeService gebruikt HttpClient, en HttpClient deze interceptor
  private readonly injector = inject(Injector);
  private lastReadOnlyToastAt = 0;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(tap({ error: (err: unknown) => this.onError(err) }));
  }

  private onError(err: unknown): void {
    if (!(err instanceof HttpErrorResponse)) {
      return;
    }
    const code = platformModeRefusal(err);
    if (code === undefined) {
      return;
    }
    void this.injector.get(PlatformModeService).load();
    if (code === EgiErrorCode.PlatformReadOnly) {
      this.notifyReadOnly();
    }
  }

  private notifyReadOnly(): void {
    const now = Date.now();
    if (now - this.lastReadOnlyToastAt < READ_ONLY_TOAST_INTERVAL_MS) {
      return;
    }
    this.lastReadOnlyToastAt = now;
    const message = this.injector.get(TranslocoService).translate('platform.mode.readOnlyRefused');
    this.injector.get(NOTIFIER).warning(message);
  }
}
